import { supabase } from '../lib/supabase';

class RespostasService {
  // Buscar todos os testes realizados com detalhes completos
  async getTestesRealizados() {
    try {
      const { data, error } = await supabase
        .from('teste_execucoes')
        .select(`
          id,
          teste_id,
          aluno_id,
          data_inicio,
          data_fim,
          duracao_minutos,
          pontuacao_obtida,
          questoes_corretas,
          questoes_total,
          percentual_acerto,
          status_execucao,
          status_aprovacao,
          respostas,
          testes!inner(
            nome,
            materia_id,
            ano_escolar_id,
            pontuacao_maxima,
            nota_minima_aprovacao,
            materias(nome),
            anos_escolares(nome)
          ),
          alunos!inner(
            nome,
            email,
            escola_id
          )
        `)
        .eq('status_execucao', 'finalizado')
        .order('data_fim', { ascending: false });

      if (error) {
        console.error('Erro ao buscar testes realizados:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Erro no service getTestesRealizados:', error);
      throw error;
    }
  }

  // Buscar detalhes de um teste específico com questões e respostas
  async getDetalhesTeste(execucaoId) {
    try {
      // Buscar execução com dados do teste e aluno
      const { data: execucao, error: execucaoError } = await supabase
        .from('teste_execucoes')
        .select(`
          id,
          teste_id,
          aluno_id,
          data_inicio,
          data_fim,
          duracao_minutos,
          questoes_corretas,
          questoes_total,
          percentual_acerto,
          pontuacao_obtida,
          status_execucao,
          status_aprovacao,
          respostas,
          testes!inner(
            nome,
            descricao,
            materia_id,
            ano_escolar_id,
            pontuacao_maxima,
            nota_minima_aprovacao,
            materias(nome),
            anos_escolares(nome)
          ),
          alunos!inner(
            nome,
            email,
            escola_id
          )
        `)
        .eq('id', execucaoId)
        .single();

      if (execucaoError) {
        console.error('Erro ao buscar execução:', execucaoError);
        throw execucaoError;
      }

      // Buscar questões do teste
      const { data: questoes, error: questoesError } = await supabase
        .from('teste_questoes')
        .select(`
          id,
          questao,
          tipo_questao,
          alternativas,
          resposta_correta,
          ordem
        `)
        .eq('teste_id', execucao.teste_id)
        .eq('ativo', true)
        .order('ordem');

      if (questoesError) {
        console.error('Erro ao buscar questões:', questoesError);
        throw questoesError;
      }

      return {
        execucao,
        questoes: questoes || []
      };
    } catch (error) {
      console.error('Erro no service getDetalhesTeste:', error);
      throw error;
    }
  }

  // Buscar estatísticas gerais
  async getEstatisticas() {
    try {
      const { data, error } = await supabase
        .from('teste_execucoes')
        .select('status_execucao, status_aprovacao, percentual_acerto')
        .eq('status_execucao', 'finalizado');

      if (error) {
        console.error('Erro ao buscar estatísticas:', error);
        throw error;
      }

      const total = data.length;
      const aprovados = data.filter(t => t.status_aprovacao === 'aprovado').length;
      const reprovados = total - aprovados;
      const mediaPercentual = total > 0 
        ? data.reduce((acc, t) => acc + (t.percentual_acerto || 0), 0) / total 
        : 0;

      return {
        total,
        aprovados,
        reprovados,
        mediaPercentual: Math.round(mediaPercentual * 10) / 10
      };
    } catch (error) {
      console.error('Erro no service getEstatisticas:', error);
      throw error;
    }
  }

  // Buscar testes por filtros
  async getTestesFiltrados(filtros = {}) {
    try {
      let query = supabase
        .from('teste_execucoes')
        .select(`
          id,
          teste_id,
          aluno_id,
          data_inicio,
          data_fim,
          duracao_minutos,
          questoes_corretas,
          questoes_total,
          percentual_acerto,
          pontuacao_obtida,
          status_execucao,
          status_aprovacao,
          testes!inner(
            nome,
            materia_id,
            ano_escolar_id,
            materias(nome),
            anos_escolares(nome)
          ),
          alunos!inner(
            nome,
            email,
            escola_id
          )
        `)
        .eq('status_execucao', 'finalizado');

      // Aplicar filtros
      if (filtros.alunoId) {
        query = query.eq('aluno_id', filtros.alunoId);
      }

      if (filtros.testeId) {
        query = query.eq('teste_id', filtros.testeId);
      }

      if (filtros.materiaId) {
        query = query.eq('testes.materia_id', filtros.materiaId);
      }

      if (filtros.anoEscolarId) {
        query = query.eq('testes.ano_escolar_id', filtros.anoEscolarId);
      }

      if (filtros.aprovado !== undefined) {
        const statusAprovacao = filtros.aprovado === 'true' ? 'aprovado' : 'reprovado';
        query = query.eq('status_aprovacao', statusAprovacao);
      }

      if (filtros.dataInicio) {
        query = query.gte('data_fim', filtros.dataInicio);
      }

      if (filtros.dataFim) {
        query = query.lte('data_fim', filtros.dataFim);
      }

      // Ordenação
      query = query.order('data_fim', { ascending: false });

      const { data, error } = await query;

      if (error) {
        console.error('Erro ao buscar testes filtrados:', error);
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Erro no service getTestesFiltrados:', error);
      throw error;
    }
  }

  // Formatar tempo em formato legível
  formatarTempo(minutos) {
    if (!minutos) return '0min';
    
    const horas = Math.floor(minutos / 60);
    const mins = minutos % 60;

    if (horas > 0) {
      return `${horas}h ${mins}min`;
    } else {
      return `${mins}min`;
    }
  }

  // Formatar data brasileira
  formatarData(data) {
    if (!data) return '-';
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
}

export default new RespostasService();
